import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";
import Sidebar from "./Sidebar";
import NotificationsDropdown from "./NotificationsDropdown";
import { profileService, notificationService } from "../services/api.service";
import logger from "../utils/logger";
import AIAssistant from "./ai/AIAssistant";


const Layout = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileData, setProfileData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadNotificationsCount();
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await profileService.getProfile();
      logger.debug("📥 Layout - Profile response:", response);

      if (response && response.success && response.data) {
        setProfileData(response.data);
        logger.info("✅ Layout - Profile updated:", response.data.name);
      }
    } catch (error) {
      logger.error("❌ Layout - Error fetching profile:", error);
    }
  };

  const fetchUnreadNotificationsCount = async () => {
    try {
      const { count } = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      logger.error("Error fetching unread notifications count:", error);
      setUnreadCount(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Top Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 h-20 bg-white shadow-sm z-30 lg:pr-72">
        <div className="flex items-center justify-between h-full px-4">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-600 hover:bg-gray-100 p-2 rounded-lg"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  isMobileMenuOpen
                    ? "M6 18L18 6M6 6l12 12"
                    : "M4 6h16M4 12h16M4 18h16"
                }
              />
            </svg>
          </button>

          {/* Page Title */}
          <div className="flex-1 text-center lg:text-right">
            <h1 className="text-lg font-semibold text-gray-800">
              
               <span className="text-[#d4af37]">"المرجع برو"</span> لإدارة مكاتب المحاماة
            </h1>
          </div>

          {/* User Name Only */}
          <div className="ml-4 hidden lg:flex items-center gap-2 text-sm text-gray-600">
            <UserOutlined className="text-lg" />
            <span className="font-medium">
              {profileData?.name || "المستخدم"}
            </span>
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full relative"
            >
              <span className="text-xl">🔔</span>
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            <NotificationsDropdown
              isOpen={isNotificationsOpen}
              onClose={() => {
                setIsNotificationsOpen(false);
                fetchUnreadNotificationsCount(); // تحديث العدد بعد إغلاق القائمة
              }}
            />
          </div>
        </div>
      </div>

      {/* Sidebar - Mobile Optimized */}
      <div
        className={`
        fixed top-0 right-0 h-full w-72 z-40 bg-white
        transform transition-transform duration-300 ease-in-out
        ${
          isMobileMenuOpen
            ? "translate-x-0"
            : "translate-x-full lg:translate-x-0"
        }
      `}
      >
        <Sidebar onClose={() => setIsMobileMenuOpen(false)} />
      </div>

      {/* Main Content - Responsive Padding */}
      <main className="transition-all duration-300 lg:mr-72 pt-20 px-4 lg:px-8 pb-8">
        {children}
      </main>

      {/* Mobile Backdrop with Better Touch Response */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* AI Smart Assistant */}
      <AIAssistant />
    </div>
  );
};

export default Layout;
